import { Helmet } from 'react-helmet-async';
import { COMPANY } from '../../lib/constants';

interface SchemaMarkupProps {
  type: 'organization' | 'localBusiness' | 'website' | 'webpage' | 'service' | 'faqPage' | 'article';
  pageUrl?: string;
  pageTitle?: string;
  pageDescription?: string;
  breadcrumb?: Array<{ name: string; url: string }>;
  articleMeta?: {
    headline: string;
    description: string;
    datePublished: string;
    dateModified: string;
    slug: string;
  };
  faqItems?: Array<{ question: string; answer: string }>;
}

export function SchemaMarkup({
  type,
  pageUrl = 'https://puntuit.nl',
  pageTitle,
  pageDescription,
  breadcrumb,
  articleMeta,
  faqItems,
}: SchemaMarkupProps) {
  const baseUrl = 'https://puntuit.nl';

  // E.164-genormaliseerd telefoonnummer (bv. "085 – 107 12 56" -> "+31851071256")
  const phoneE164 = '+31' + COMPANY.phone.replace(/\D/g, '').replace(/^0/, '');

  // constants gebruiken het formaat "POSTCODE, STAD" (bv. "1112 VA, Diemen")
  const toPostalAddress = (addr: { street: string; city: string }) => {
    const [postalCode, addressLocality] = addr.city.split(',').map((s) => s.trim());
    return {
      '@type': 'PostalAddress',
      streetAddress: addr.street,
      postalCode,
      addressLocality,
      addressCountry: 'NL',
    };
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: COMPANY.name,
    url: baseUrl,
    logo: `${baseUrl}/images/puntuit-logo.png`,
    description: COMPANY.tagline,
    email: COMPANY.email,
    telephone: phoneE164,
    address: COMPANY.addresses.map(toPostalAddress),
    sameAs: [
      'https://www.linkedin.com/company/puntuit',
    ],
  };

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: COMPANY.name,
    url: baseUrl,
    logo: `${baseUrl}/images/puntuit-logo.png`,
    description: COMPANY.tagline,
    email: COMPANY.email,
    telephone: phoneE164,
    address: toPostalAddress(COMPANY.addresses[0]),
    areaServed: 'NL',
    serviceType: 'Externe Vertrouwenspersoon',
    priceRange: '€€',
    openingHours: ['Mo-Fr 09:00-21:00'],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Vertrouwenspersoon Diensten',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Gratis Spreekuur',
            description: 'Werknemers kunnen gratis gebruik maken van ons spreekuur',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Zelfhulpplatform',
            description: 'Online zelfhulpplatform met informatie en afsprakenplanner voor medewerkers',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Zelfhulpplatform en bereikbaarheid',
            description: '24/7 toegang tot het zelfhulpplatform en telefonisch/WhatsApp bereikbaar op werkdagen van 9:00 tot 21:00',
          },
        },
      ],
    },
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: COMPANY.name,
    url: baseUrl,
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle || COMPANY.name,
    description: pageDescription || COMPANY.tagline,
    url: pageUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: COMPANY.name,
      url: baseUrl,
    },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: `${baseUrl}/images/puntuit-logo.png`,
    },
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Externe Vertrouwenspersoon',
    provider: {
      '@type': 'Organization',
      name: COMPANY.name,
      url: baseUrl,
    },
    description: 'PuntUit biedt externe vertrouwenspersonen met een unieke aanpak gericht op bewustwording, preventie en laagdrempelige bereikbaarheid.',
    areaServed: 'Nederland',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Pakketten',
      itemListElement: [
        {
          '@type': 'Offer',
          price: '375',
          priceCurrency: 'EUR',
          priceValidUntil: '2026-12-31',
          itemOffered: {
            '@type': 'Service',
            name: 'Tot en met 10 personen',
            description: 'Jaarabonnement voor organisaties tot en met 10 personen',
          },
        },
        {
          '@type': 'Offer',
          price: '475',
          priceCurrency: 'EUR',
          priceValidUntil: '2026-12-31',
          itemOffered: {
            '@type': 'Service',
            name: '11 t/m 250 personen',
            description: 'Jaarabonnement voor organisaties van 11 t/m 250 personen',
          },
        },
        {
          '@type': 'Offer',
          price: '950',
          priceCurrency: 'EUR',
          priceValidUntil: '2026-12-31',
          itemOffered: {
            '@type': 'Service',
            name: 'Tot en met 500 personen',
            description: 'Jaarabonnement, + € 475 per extra 250 personen',
          },
        },
      ],
    },
  };

  const breadcrumbSchema = breadcrumb
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumb.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`,
        })),
      }
    : null;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: (faqItems || []).map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const articleUrl = articleMeta ? `${baseUrl}/kennisbank/${articleMeta.slug}` : baseUrl;
  const articleSchema = articleMeta
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: articleMeta.headline,
        description: articleMeta.description,
        image: `${baseUrl}/images/extern-vertrouwenspersoon.jpg`,
        inLanguage: 'nl-NL',
        url: articleUrl,
        datePublished: articleMeta.datePublished,
        dateModified: articleMeta.dateModified,
        author: {
          '@type': 'Organization',
          name: COMPANY.name,
          url: baseUrl,
        },
        publisher: {
          '@type': 'Organization',
          name: COMPANY.name,
          logo: {
            '@type': 'ImageObject',
            url: `${baseUrl}/images/puntuit-logo.png`,
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': articleUrl,
        },
      }
    : null;

  let schema;
  switch (type) {
    case 'organization':
      schema = organizationSchema;
      break;
    case 'localBusiness':
      schema = localBusinessSchema;
      break;
    case 'website':
      schema = websiteSchema;
      break;
    case 'webpage':
      schema = webpageSchema;
      break;
    case 'service':
      schema = serviceSchema;
      break;
    case 'faqPage':
      schema = faqSchema;
      break;
    case 'article':
      schema = articleSchema;
      break;
    default:
      schema = organizationSchema;
  }

  return (
    <Helmet>
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
}
